import React, { useState } from 'react';
import { X, User, Phone, Calendar, MapPin, Home, Users } from 'lucide-react';
import { apiFetch } from '../../utils/apiClient';

const PatientRegistrationModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    age: '',
    gender: '',
    pincode: '',
    city: '',
    fullAddress: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }
    if (!formData.age) newErrors.age = 'Age is required';
    else if (formData.age < 0 || formData.age > 150) newErrors.age = 'Please enter a valid age';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(formData.pincode)) newErrors.pincode = 'Please enter a valid 6-digit pincode';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.fullAddress.trim()) newErrors.fullAddress = 'Address is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      setRegistrationSuccess(null);

      try {
        const response = await apiFetch('/backend/api/register-patient', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            fullName: formData.name,
            phoneNumber: formData.phoneNumber,
            age: formData.age,
            gender: formData.gender,
            pincode: formData.pincode,
            city: formData.city,
            fullAddress: formData.fullAddress
          })
        });

        if (response.status === 401) {
          alert('Your session has expired. Please log in again to register a patient.');
          return;
        }

        const data = await response.json();

        if (response.ok && data.success) {
          setRegistrationSuccess({
            uhid: data.uhid,
            message: data.message,
            isExisting: data.isExisting
          });

          // Show different messages for new vs existing patients
          if (data.isExisting) {
            alert(`Patient already registered!\nUHID: ${data.uhid}\n\nPatient details have been updated.`);
          } else {
            alert(`New patient registered successfully!\nUHID: ${data.uhid}\n\nPlease save this UHID for future reference.`);
          }

          // Call onSuccess callback to refresh UHID list and notify chat
          if (onSuccess) {
            console.log('✅ Calling onSuccess callback with UHID:', data.uhid);
            onSuccess(data.uhid);
          }

          // Reset form after short delay
          setTimeout(() => {
            setFormData({
              name: '',
              phoneNumber: '',
              age: '',
              gender: '',
              pincode: '',
              city: '',
              fullAddress: ''
            });
            setRegistrationSuccess(null);
            onClose();
          }, 2000);
        } else {
          // Check if it's a duplicate patient error
          if (data.error && (data.error.includes('Already Exist') || data.error.includes('already exist'))) {
            setErrors({
              name: 'Patient already exists with associated phone number.',
            });
            alert(`Registration failed: ${data.error}\n\nPlease use a different patient name.`);
            return;
          } else {
            throw new Error(data.error || 'Registration failed');
          }
        }
      } catch (error) {
        console.error('Registration error:', error);
        alert(`Registration failed: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold">New Patient Registration</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Success Message */}
        {registrationSuccess && (
          <div className={`border px-4 py-3 mx-6 mt-4 rounded-lg ${
            registrationSuccess.isExisting 
              ? 'bg-blue-50 border-blue-200 text-blue-800' 
              : 'bg-green-50 border-green-200 text-green-800'
          }`}>
            <p className="font-semibold">
              {registrationSuccess.isExisting ? 'Existing Patient Found!' : 'Registration Successful!'}
            </p>
            <p>UHID: <span className="font-mono font-bold">{registrationSuccess.uhid}</span></p>
            {registrationSuccess.isExisting && (
              <p className="text-sm mt-1">Patient details have been updated.</p>
            )}
          </div>
        )}

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name Field */}
          <div>
            <label className="flex items-center space-x-2 text-gray-700 font-medium mb-2">
              <User className="w-4 h-4" />
              <span>Full Name *</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter patient's full name"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Phone Number Field */}
          <div>
            <label className="flex items-center space-x-2 text-gray-700 font-medium mb-2">
              <Phone className="w-4 h-4" />
              <span>Phone Number *</span>
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter 10-digit phone number"
            />
            {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
          </div>

          {/* Age and Gender Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Age Field */}
            <div>
              <label className="flex items-center space-x-2 text-gray-700 font-medium mb-2">
                <Calendar className="w-4 h-4" />
                <span>Age *</span>
              </label>
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.age ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter age"
                min="0"
                max="150"
              />
              {errors.age && <p className="text-red-500 text-sm mt-1">{errors.age}</p>}
            </div>

            {/* Gender Field */}
            <div>
              <label className="flex items-center space-x-2 text-gray-700 font-medium mb-2">
                <Users className="w-4 h-4" />
                <span>Gender *</span>
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.gender ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
            </div>
          </div>

          {/* Pincode and City Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Pincode Field */}
            <div>
              <label className="flex items-center space-x-2 text-gray-700 font-medium mb-2">
                <MapPin className="w-4 h-4" />
                <span>Pincode *</span>
              </label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                maxLength="6"
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.pincode ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="6-digit pincode"
              />
              {errors.pincode && <p className="text-red-500 text-sm mt-1">{errors.pincode}</p>}
            </div>

            {/* City Field */}
            <div>
              <label className="flex items-center space-x-2 text-gray-700 font-medium mb-2">
                <MapPin className="w-4 h-4" />
                <span>City *</span>
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.city ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter city"
              />
              {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
            </div>
          </div>

          {/* Full Address Field */}
          <div>
            <label className="flex items-center space-x-2 text-gray-700 font-medium mb-2">
              <Home className="w-4 h-4" />
              <span>Full Address *</span>
            </label>
            <textarea
              name="fullAddress"
              value={formData.fullAddress}
              onChange={handleChange}
              rows="3"
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.fullAddress ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter complete address"
            />
            {errors.fullAddress && <p className="text-red-500 text-sm mt-1">{errors.fullAddress}</p>}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-6 py-3 text-white rounded-lg transition-colors ${
                isLoading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isLoading ? 'Registering...' : 'Register Patient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientRegistrationModal;